import React, { useMemo, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { AlertTriangle, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WizardStepProps } from "@components/WizardCst/types";
import LOPickerCst, { LOPickerSelectedItem } from "@components/LOPickerCst";
import SelectedLOList from "@components/SelectedLOList";
import { CourseWizardData, SelectedActivity } from "../types";

interface AddActivitiesStepProps extends WizardStepProps<CourseWizardData> {
  lockAdditions?: boolean;
}

const AddActivitiesStep: React.FC<AddActivitiesStepProps> = ({
  data,
  setData,
  lockAdditions = false,
}) => {
  const { t } = useTranslation("wizard-course");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [pickerOpen, setPickerOpen] = useState(false);

  const activities = data.selected_activities;
  const count = activities.length;
  const isEmpty = count === 0;

  const excludeIds = useMemo(() => activities.map((a) => a.id), [activities]);

  const handlePickerConfirm = (added: LOPickerSelectedItem[]) => {
    if (added.length === 0) return;

    const known = new Set(activities.map((a) => a.id));
    const fresh: SelectedActivity[] = added
      .filter((a) => !known.has(a.id))
      .map((a) => ({
        id: a.id,
        title: a.title,
        type: a.type,
        kind: "activity",
        entity: a.entity,
      }));
    setData({ selected_activities: [...activities, ...fresh] });
  };

  const handleRemove = (id: number) => {
    setData({
      selected_activities: activities.filter((a) => a.id !== id),
    });
  };

  // TODO: integrare la creazione di una nuova attività dallo step 3

  const handleCreateNew = () => {
    console.info("[course-wizard] crea nuova attività — TODO");
  };

  // TODO: pagina dettaglio v2 attività non disponibile oggi.
  const handleDetails = (id: number) => {
    console.info("[course-wizard] activity details — TODO", id);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {isEmpty && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "12px 16px",
            borderRadius: "12px",
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.dark,
            border: `1px solid ${theme.palette.warning.main}`,
          }}
        >
          <AlertTriangle size={20} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: theme.palette.common.black,
            }}
          >
            {t("step-activities.empty-warning")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Button
          onClick={() => setPickerOpen(true)}
          variant="contained"
          startIcon={<Plus size={16} />}
          disabled={lockAdditions}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("step-activities.add")}
        </Button>
        <Button
          onClick={handleCreateNew}
          variant="outlined"
          startIcon={<Plus size={16} />}
          disabled={lockAdditions}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          }}
        >
          {t("step-activities.create-new")}
        </Button>
      </Box>

      <Typography
        sx={{
          fontSize: "14px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {t("step-activities.counter", { count })}
      </Typography>

      {!isEmpty && (
        <SelectedLOList
          items={activities}
          tCatalog={tCatalog}
          removeLabel={t("step-activities.remove")}
          onRemove={handleRemove}
          onDetails={(item) => handleDetails(item.id)}
        />
      )}

      <LOPickerCst
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t("picker-activities.title")}
        entityType="activity"
        excludeIds={excludeIds}
        onConfirm={handlePickerConfirm}
      />
    </Box>
  );
};

export default AddActivitiesStep;
